import React, { useState, useEffect } from "react";
import {
  Heart,
  Book,
  Award,
  Users,
  TrendingUp,
  Flame,
  ChevronDown,
} from "lucide-react";
import { getAvatarGreeting } from "../lib/streakUtils";
import { getAvatarMoodFromStats, getStatColor, getStatLabel } from "../lib/tamagotchiUtils";
import { heartModuleOverview } from "../data/modules/heart/heartModuleOverview";
import { brainModuleOverview } from "../data/modules/brain/brainModuleOverview";
import { lungsModuleOverview } from "../data/modules/lungs/lungsModuleOverview";
import { colonModuleOverview } from "../data/modules/colon/colonModuleOverview";
import { liverModuleOverview } from "../data/modules/liver/liverModuleOverview";
import { kidneyModuleOverview } from "../data/modules/kidney/kidneyModuleOverview";
import { womenreproductiveModuleOverview } from "../data/modules/womenreproductive/womenreproductiveModuleOverview";
import { thyroidModuleOverview } from "../data/modules/thyroid/thyroidModuleOverview";
import { skeletonModuleOverview } from "../data/modules/skeleton/skeletonModuleOverview";
import { adrenalglandModuleOverview } from "../data/modules/adrenalglands/adrenalglandModuleOverview";
import { usmle1ModuleOverview } from "../data/modules/usmle1/usmle1ModuleOverview";
import { usmle2ModuleOverview } from "../data/modules/usmle2/usmle2ModuleOverview";
import { geneticsModuleOverview } from "../data/modules/genetics/geneticsModuleOverview";
import AvatarPreview from "./AvatarPreview";
import { usmle1Facts } from "../data/usmle1Facts";
import { loadLeaderboardData } from "../lib/database";
import { calcMomentum } from "../lib/leagueUtils";

const Dashboard = ({ userData, onViewChange, userId }) => {
  // Calculate avatar mood and filter from tamagotchi stats
  const statMood = getAvatarMoodFromStats(userData.avatarStats);
  const moodExpression = { eyebrows: statMood.eyebrows, mouth: statMood.mouth };
  const greeting = getAvatarGreeting(
    userData.username,
    userData.avatarStats,
    userData.streakData?.currentStreak || 0
  );

  const [isCalendarOpen, setIsCalendarOpen] = useState(true);
  const [calendarOffset, setCalendarOffset] = useState(0); // 0 = current month, -1 = last month, etc.

  // Build check-in calendar data for the viewed month
  const buildCalendar = () => {
    const now = new Date();
    const viewDate = new Date(now.getFullYear(), now.getMonth() + calendarOffset, 1);
    const year = viewDate.getFullYear();
    const month = viewDate.getMonth();
    const monthName = viewDate.toLocaleString("default", { month: "long", year: "numeric" });
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const firstDayOffset = (new Date(year, month, 1).getDay() + 6) % 7;

    // Use permanent activity dates from streakData
    const activityDates = userData.streakData?.activityDates || [];
    const checkedIn = new Set(
      activityDates
        .filter(d => {
          const [y, m] = d.split("-").map(Number);
          return y === year && m - 1 === month;
        })
        .map(d => Number(d.split("-")[2]))
    );

    const todayDate = calendarOffset === 0 ? now.getDate() : null;

    const cells = [
      ...Array(firstDayOffset).fill(null),
      ...Array.from({ length: daysInMonth }, (_, i) => i + 1),
    ];

    return { cells, checkedIn, todayDate, monthName };
  };
  const calendar = buildCalendar();

  // Helper function to find the current organ and module the user is working on
  const getCurrentStudy = () => {
    // Find the organ and module with the most recent progress (in-progress or incomplete)
    const organsList = [
      { id: "heart", name: "Heart", modules: heartModuleOverview },
      { id: "brain", name: "Brain", modules: brainModuleOverview },
      { id: "lungs", name: "Lungs", modules: lungsModuleOverview },
      { id: "colon", name: "Colon", modules: colonModuleOverview },
      { id: "liver", name: "Liver", modules: liverModuleOverview },
      { id: "kidney", name: "Kidneys", modules: kidneyModuleOverview },
      {
        id: "womenreproductive",
        name: "Woman Reproductive",
        modules: womenreproductiveModuleOverview,
      },
      { id: "skeleton", name: "Skeleton", modules: skeletonModuleOverview },
      { id: "thyroid", name: "Thyroid Gland", modules: thyroidModuleOverview },
      {
        id: "adrenalgland",
        name: "Adrenal Gland",
        modules: adrenalglandModuleOverview,
      },
    ];

    // First, find the most recently accessed module (based on timestamp)
    let mostRecentModule = null;
    let mostRecentTimestamp = null;

    for (const organ of organsList) {
      const progress = userData.organProgress[organ.id];
      if (!progress || !progress.moduleData) continue;

      // Look through all modules for this organ
      for (let i = 0; i < organ.modules.length; i++) {
        const module = organ.modules[i];
        const moduleProgress = progress.moduleData[module.id];

        // A module is "in progress" if:
        // 1. It has a currentSection defined (even if 0)
        // 2. OR it has saved answers
        // 3. AND it's not marked as completed
        const hasCurrentSection = moduleProgress && typeof moduleProgress.currentSection === 'number';
        const hasSavedAnswers = moduleProgress && moduleProgress.sectionAnswers &&
                                Object.keys(moduleProgress.sectionAnswers).length > 0;
        const isNotCompleted = !moduleProgress?.completed;

        const isInProgress = (hasCurrentSection || hasSavedAnswers) && isNotCompleted;

        if (isInProgress && moduleProgress.lastAccessedAt) {
          const accessTimestamp = new Date(moduleProgress.lastAccessedAt);

          // Check if this is the most recent module accessed
          if (!mostRecentTimestamp || accessTimestamp > mostRecentTimestamp) {
            mostRecentTimestamp = accessTimestamp;
            const completedCount = progress?.completed?.length || 0;
            mostRecentModule = {
              organId: organ.id,
              organName: organ.name,
              moduleName: module?.title || "Current Module",
              moduleId: module?.id || "",
              customImage: module?.customImage || null,
              moduleIndex: i,
              completedCount,
              totalModules: organ.modules.length,
            };
          }
        }
      }
    }

    // If we found a recently accessed in-progress module, return it
    if (mostRecentModule) {
      return mostRecentModule;
    }

    // If no in-progress modules found, find the first incomplete module
    for (const organ of organsList) {
      const progress = userData.organProgress[organ.id];
      const completedCount = progress?.completed?.length || 0;
      const totalModules = organ.modules.length;

      if (completedCount < totalModules) {
        const nextModule = organ.modules[completedCount];
        return {
          organId: organ.id,
          organName: organ.name,
          moduleName: nextModule?.title || "Next Module",
          moduleId: nextModule?.id || "",
          customImage: nextModule?.customImage || null,
          moduleIndex: completedCount, // Index of the module to navigate to
          completedCount,
          totalModules,
        };
      }
    }

    // All modules completed
    return null;
  };

  const currentStudy = getCurrentStudy();

  // Find modules due for spaced-repetition review
  const getRecommendedReviews = () => {
    const organsList = [
      { id: "heart", name: "Heart", customImage: "organ-heart.png", modules: heartModuleOverview },
      { id: "brain", name: "Brain", customImage: "organ-brain.png", modules: brainModuleOverview },
      { id: "lungs", name: "Lungs", customImage: "organ-lungs.png", modules: lungsModuleOverview },
      { id: "colon", name: "Colon", customImage: "organ-colon.png", modules: colonModuleOverview },
      { id: "liver", name: "Liver", customImage: "organ-liver.png", modules: liverModuleOverview },
      { id: "kidney", name: "Kidneys", customImage: "organ-kidney.png", modules: kidneyModuleOverview },
      { id: "womenreproductive", name: "Reproductive", customImage: "organ-uterus.png", modules: womenreproductiveModuleOverview },
      { id: "skeleton", name: "Skeleton", customImage: "organ-bone.png", modules: skeletonModuleOverview },
      { id: "thyroid", name: "Thyroid", customImage: "organ-thyroid.png", modules: thyroidModuleOverview },
      { id: "adrenalgland", name: "Adrenal Gland", customImage: "organ-adrenalgland.png", modules: adrenalglandModuleOverview },
    ];

    const now = new Date();
    const due = [];
    let nextUpcoming = null;

    for (const organ of organsList) {
      const progress = userData.organProgress[organ.id];
      if (!progress?.moduleData) continue;

      for (let i = 0; i < organ.modules.length; i++) {
        const module = organ.modules[i];
        const mp = progress.moduleData[module.id];
        if (!mp?.completed || !mp?.nextReviewDate) continue;

        const reviewDate = new Date(mp.nextReviewDate);

        if (now >= reviewDate) {
          const daysOverdue = Math.floor((now - reviewDate) / (1000 * 60 * 60 * 24));
          due.push({
            organId: organ.id,
            organName: organ.name,
            moduleImage: module.customImage || organ.customImage,
            moduleId: module.id,
            moduleName: module.title,
            moduleIndex: i,
            daysOverdue,
          });
        } else if (!nextUpcoming || reviewDate < nextUpcoming.reviewDate) {
          nextUpcoming = {
            moduleName: module.title,
            organName: organ.name,
            reviewDate,
          };
        }
      }
    }

    due.sort((a, b) => b.daysOverdue - a.daysOverdue);
    return { due, nextUpcoming };
  };

  const { due: reviewsDue, nextUpcoming } = getRecommendedReviews();

  // Medical rank milestones based on streak days
  const STREAK_RANKS = [
    { days: 0,   title: "Pre-Med Student",         emoji: "🎓" },
    { days: 10,  title: "MS1 — First Year",         emoji: "📚" },
    { days: 20,  title: "MS2 — Second Year",        emoji: "📖" },
    { days: 35,  title: "MS3 — Clinical Rotations", emoji: "🩺" },
    { days: 50,  title: "MS4 — Senior Student",     emoji: "📋" },
    { days: 65,  title: "Medical Graduate",          emoji: "🎓" },
    { days: 80,  title: "Intern",                   emoji: "🏥" },
    { days: 110, title: "Junior Resident",           emoji: "💊" },
    { days: 140, title: "Senior Resident",           emoji: "🔬" },
    { days: 170, title: "Chief Resident",            emoji: "⚕️" },
    { days: 210, title: "Clinical Fellow",           emoji: "🧬" },
    { days: 280, title: "Attending Physician",       emoji: "👨‍⚕️" },
    { days: 365, title: "Specialist",               emoji: "⭐" },
    { days: 500, title: "Consultant",               emoji: "🌟" },
    { days: 730, title: "Professor of Medicine",    emoji: "👑" },
  ];

  const getStreakRank = () => {
    const streak = userData.streakData?.currentStreak || 0;
    let currentRank = STREAK_RANKS[0];
    let nextRank = null;
    for (let i = 0; i < STREAK_RANKS.length; i++) {
      if (streak >= STREAK_RANKS[i].days) {
        currentRank = STREAK_RANKS[i];
        nextRank = STREAK_RANKS[i + 1] || null;
      } else {
        break;
      }
    }
    const daysToNext = nextRank ? nextRank.days - streak : 0;
    const rangeStart = currentRank.days;
    const rangeEnd = nextRank ? nextRank.days : currentRank.days;
    const progress = nextRank
      ? Math.round(((streak - rangeStart) / (rangeEnd - rangeStart)) * 100)
      : 100;
    return { currentRank, nextRank, daysToNext, progress, streak };
  };

  const streakRank = getStreakRank();

  // Real leaderboard top-3 from the user's league group
  const [topUsers, setTopUsers] = useState([]);
  useEffect(() => {
    if (!userId) return;
    loadLeaderboardData(userId)
      .then(({ group }) => {
        const sorted = [...group].sort(
          (a, b) => calcMomentum(b.weeklyXP, b.streak) - calcMomentum(a.weeklyXP, a.streak)
        );
        setTopUsers(sorted.slice(0, 3));
      })
      .catch(() => {}); // silently fail — preview is non-critical
  }, [userId]);

  // Daily biology facts
  const biologyFacts = [
    "The human heart beats about 100,000 times per day.",
    "Your brain uses 20% of your body's total oxygen and energy.",
    "The human body contains about 37.2 trillion cells.",
    "Red blood cells live for about 120 days before being replaced.",
    "The liver performs over 500 different functions in the body.",
    "Your lungs contain about 1,500 miles of airways.",
    "Bones are about 5 times stronger than steel of the same density.",
    "The human stomach produces a new layer of mucus every two weeks.",
    "Your eyes can distinguish approximately 10 million different colors.",
    "The average person produces about 25,000 quarts of saliva in a lifetime.",
    "Nerve impulses can travel at speeds up to 250 mph (402 km/h).",
    "The human body has enough iron to make a 3-inch (7.6 cm) nail.",
    "Your kidneys filter about 200 quarts of blood daily.",
    "The small intestine is about 20 feet long in adults.",
    "Humans shed about 600,000 particles of skin every hour.",
    "When the adrenal glands are corrected for weight, they receive amont the highest blood flow of any organ in the body.",
  ];

  // Get a daily fact based on the current date
  const getDailyFact = () => {
    const today = new Date();
    const dayOfYear = Math.floor(
      (today - new Date(today.getFullYear(), 0, 0)) / 1000 / 60 / 60 / 24,
    );
    return biologyFacts[dayOfYear % biologyFacts.length];
  };

  const organs = [
    {
      id: "heart",
      name: "Heart",
      icon: Heart,
      customImage: "organ-heart.png",
      gradient: "from-red-100 via-rose-50 to-pink-100",
      iconColor: "bg-red-100 text-red-600",
      borderColor: "border-red-300",
      hoverGradient: "hover:from-red-200 hover:to-pink-200",
      totalModules: heartModuleOverview.length,
    },
    {
      id: "brain",
      name: "Brain",
      icon: Book,
      customImage: "organ-brain.png",
      gradient: "from-purple-100 via-violet-50 to-indigo-100",
      iconColor: "bg-purple-100 text-purple-600",
      borderColor: "border-purple-300",
      hoverGradient: "hover:from-purple-200 hover:to-indigo-200",
      totalModules: brainModuleOverview.length,
      comingSoon: true,
    },
    {
      id: "lungs",
      name: "Lungs",
      icon: Book,
      customImage: "organ-lungs.png",
      gradient: "from-blue-100 via-sky-50 to-cyan-100",
      iconColor: "bg-blue-100 text-blue-600",
      borderColor: "border-blue-300",
      hoverGradient: "hover:from-blue-200 hover:to-cyan-200",
      totalModules: lungsModuleOverview.length,
    },
    // Liver
    {
      id: "liver",
      name: "Liver",
      icon: Book,
      customImage: "organ-liver.png",
      gradient: "from-amber-100 via-yellow-50 to-orange-100",
      iconColor: "bg-amber-100 text-amber-700",
      borderColor: "border-amber-300",
      hoverGradient: "hover:from-amber-200 hover:to-orange-200",
      totalModules: liverModuleOverview.length,
      comingSoon: true,
    },

    // Kidneys
    {
      id: "kidney",
      name: "Kidneys",
      icon: Book,
      customImage: "organ-kidney.png",
      gradient: "from-teal-100 via-emerald-50 to-green-100",
      iconColor: "bg-teal-100 text-teal-700",
      borderColor: "border-teal-300",
      hoverGradient: "hover:from-teal-200 hover:to-green-200",
      totalModules: kidneyModuleOverview.length,
    },

    // Digestive System
    {
      id: "colon",
      name: "Colon",
      icon: Book,
      customImage: "organ-colon.png",
      gradient: "from-lime-100 via-green-50 to-emerald-100",
      iconColor: "bg-lime-100 text-lime-700",
      borderColor: "border-lime-300",
      hoverGradient: "hover:from-lime-200 hover:to-emerald-200",
      totalModules: colonModuleOverview.length,
      comingSoon: true,
    },

    // Bones/Skeleton
    {
      id: "skeleton",
      name: "Skeleton",
      icon: Book,
      customImage: "organ-bone.png",
      gradient: "from-gray-100 via-slate-50 to-zinc-100",
      iconColor: "bg-gray-100 text-gray-700",
      borderColor: "border-gray-300",
      hoverGradient: "hover:from-gray-200 hover:to-zinc-200",
      totalModules: skeletonModuleOverview.length,
      comingSoon: true,
    },

    // Reproductive System
    {
      id: "womenreproductive",
      name: "Woman Reproductive",
      icon: Book,
      customImage: "organ-uterus.png",
      gradient: "from-pink-100 via-rose-50 to-fuchsia-100",
      iconColor: "bg-pink-100 text-pink-700",
      borderColor: "border-fuchsia-300",
      hoverGradient: "hover:from-fuchsia-200 hover:to-rose-200",
      totalModules: womenreproductiveModuleOverview.length,
      comingSoon: true,
    },

    // Thyroid gland
    {
      id: "thyroid",
      name: "Thyroid Gland",
      icon: Book,
      customImage: "organ-thyroid.png",
      gradient: "from-pink-100 via-purple-50 to-indigo-100",
      iconColor: "bg-pink-100 text-pink-700",
      borderColor: "border-indigo-300",
      hoverGradient: "hover:from-pink-200 hover:to-indigo-200",
      totalModules: thyroidModuleOverview.length,
    },

    // Adrenal gland
    {
      id: "adrenalgland",
      name: "Adrenal Gland",
      icon: Book,
      customImage: "organ-adrenalgland.png",
      gradient: "from-yellow-900 via-yellow-50 to-rose-100",
      iconColor: "bg-yellow-100 text-yellow-700",
      borderColor: "border-yellow-800",
      hoverGradient: "hover:from-yellow-200 hover:to-rose-200",
      totalModules: adrenalglandModuleOverview.length,
    },
  ];

  const highYieldModules = [
    {
      id: "usmle1",
      name: "USMLE1",
      icon: Book,
      gradient: "from-sky-100 via-blue-50 to-indigo-100",
      iconColor: "bg-sky-100 text-sky-700",
      borderColor: "border-blue-300",
      hoverGradient: "hover:from-sky-200 hover:to-indigo-200",
      totalModules: usmle1ModuleOverview.length,
      comingSoon: true,
    },
    {
      id: "usmle2",
      name: "USMLE2",
      icon: Book,
      gradient: "from-emerald-100 via-teal-50 to-cyan-100",
      iconColor: "bg-emerald-100 text-emerald-700",
      borderColor: "border-teal-300",
      hoverGradient: "hover:from-emerald-200 hover:to-cyan-200",
      totalModules: usmle2ModuleOverview.length,
      comingSoon: true,
    },
  ];

  const paraclinicalModules = [
    {
      id: "genetics",
      name: "Genetics",
      icon: Book,
      customImage: "organ-genetics.png",
      gradient: "from-sky-100 via-blue-50 to-indigo-100",
      iconColor: "bg-sky-100 text-sky-700",
      borderColor: "border-blue-300",
      hoverGradient: "hover:from-sky-200 hover:to-indigo-200",
      totalModules: geneticsModuleOverview.length,
    },
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <div className="grid md:grid-cols-4 gap-6 mb-8 items-start">
        {/* Avatar Card - Pokemon Card Style */}
        <div className="bg-gradient-to-br from-purple-300 via-indigo-300 to-yellow-50 rounded-xl p-4 shadow-lg border-4 border-violet-400">
          <div className="text-center mb-3">
            <h3 className="font-bold text-gray-800 text-lg">
              {userData.username || "Trainer"}
            </h3>
          </div>
          {/* Speech bubble greeting */}
          <div className="relative bg-white rounded-lg p-3 mb-3 border border-indigo-200 shadow-sm">
            <p className="text-xs text-gray-700 text-center">{greeting}</p>
            <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-3 h-3 bg-white border-b border-r border-indigo-200 transform rotate-45"></div>
          </div>
          <button
            onClick={() => onViewChange("avatar")}
            className="w-full bg-white rounded-lg p-3 mb-3 border-2 border-indigo-300 shadow-inner h-40 hover:border-indigo-400 hover:shadow-md transition-all cursor-pointer"
          >
            <AvatarPreview
              avatar={userData.equippedItems || userData.avatar}
              size="lg"
              showBackgroundFull={true}
              moodOverride={moodExpression}
              filterClass={statMood.filterClass}
            />
          </button>
          <div className="text-center text-sm font-semibold text-gray-50 bg-indigo-400 rounded-lg py-1 mb-3 border border-violet-300">
            {userData.level || "Med Student"}
          </div>
          {/* Stats Section */}
          <div className="space-y-2">
            <div className="flex items-center justify-between bg-white rounded-lg px-3 py-2 border border-violet-500">
              <div className="flex items-center gap-2">
                <Flame className="w-4 h-4 text-orange-500" />
                <span className="text-xs font-semibold text-gray-700">Streak</span>
              </div>
              <span className="text-sm font-bold text-orange-500">
                {userData.streakData?.currentStreak || 0} days
              </span>
            </div>
            <div className="flex items-center justify-between bg-white rounded-lg px-3 py-2 border border-violet-500">
              <div className="flex items-center gap-2">
                <Award className="w-4 h-4 text-teal-600" />
                <span className="text-xs font-semibold text-gray-700">XP</span>
              </div>
              <span className="text-sm font-bold text-teal-600">
                {userData.xp}
              </span>
            </div>
            <div className="flex items-center justify-between bg-white rounded-lg px-3 py-2 border border-violet-500">
              <div className="flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-yellow-600" />
                <span className="text-xs font-semibold text-gray-700">
                  Coins
                </span>
              </div>
              <span className="text-sm font-bold text-yellow-600">
                {userData.coins}
              </span>
            </div>
          </div>

          {/* Avatar Care Stats */}
          <div className="mt-2 space-y-1">
            {[
              { label: '⚡ Energy',   key: 'energy' },
              { label: '😊 Mood',     key: 'happiness' },
              { label: '🍎 Fullness', key: 'fullness' },
            ].map(({ label, key }) => {
              const value = Math.round(userData.avatarStats?.[key] ?? 80);
              const label2 = getStatLabel(value);
              return (
                <div key={key} className="bg-white rounded-lg px-3 py-1.5 border border-violet-500">
                  <div className="flex items-center justify-between mb-0.5">
                    <span className="text-xs text-gray-600">{label}</span>
                    {label2 && (
                      <span className={`text-xs font-bold ${value < 30 ? 'text-red-500' : 'text-yellow-500'}`}>
                        {label2}
                      </span>
                    )}
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-1.5">
                    <div
                      className={`h-1.5 rounded-full transition-all duration-500 ${getStatColor(value)}`}
                      style={{ width: `${value}%` }}
                    />
                  </div>
                </div>
              );
            })}
            <button
              onClick={() => onViewChange("avatar")}
              className="w-full text-xs text-violet-600 hover:text-violet-800 font-medium py-0.5 text-center"
            >
              Feed avatar →
            </button>
          </div>

        </div>

        {/* Recommended Review + Study Now Button + Daily Facts */}
        <div className="flex flex-col gap-4">
          {/* Recommended Review */}
          <div className="bg-gradient-to-br from-orange-50 to-amber-100 rounded-xl p-4 shadow-sm border-2 border-orange-300">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs font-bold text-orange-700 uppercase tracking-wide">Review Due</span>
              {reviewsDue.length > 0 && (
                <span className="text-xs font-bold bg-red-100 text-red-600 border border-red-200 px-2 py-0.5 rounded-full">
                  {reviewsDue.length} due
                </span>
              )}
            </div>

            {reviewsDue.length > 0 ? (
              <div className="flex flex-col gap-1.5 mt-2">
                {reviewsDue.slice(0, 3).map((item) => (
                  <button
                    key={item.moduleId}
                    onClick={() => onViewChange(`${item.organId}-module-${item.moduleIndex}-reset`)}
                    className="flex items-center gap-2 w-full text-left bg-white rounded-lg p-2 border border-orange-200 hover:border-orange-400 hover:shadow-sm transition-all"
                  >
                    {item.moduleImage && (
                      <div className="w-8 h-8 rounded-md overflow-hidden bg-orange-50 border border-orange-200 flex-shrink-0">
                        <img
                          src={`/${item.moduleImage}`}
                          alt={item.moduleName}
                          className="w-full h-full object-contain p-0.5"
                          onError={(e) => { e.target.style.display = "none"; }}
                        />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold text-gray-800 truncate">{item.moduleName}</p>
                      <p className="text-xs text-gray-500">{item.organName}</p>
                    </div>
                    <span className={`text-xs font-bold flex-shrink-0 ${item.daysOverdue === 0 ? "text-orange-500" : "text-red-500"}`}>
                      {item.daysOverdue === 0 ? "Today" : `${item.daysOverdue}d`}
                    </span>
                  </button>
                ))}
                {reviewsDue.length > 3 && (
                  <p className="text-xs text-orange-600 text-center font-medium mt-0.5">
                    +{reviewsDue.length - 3} more due
                  </p>
                )}
              </div>
            ) : (
              <div className="mt-2">
                <p className="text-sm font-bold text-green-600">All caught up! ✓</p>
                {nextUpcoming ? (
                  <p className="text-xs text-gray-600 mt-1">
                    Next: <span className="font-semibold text-gray-800">{nextUpcoming.moduleName}</span> on{" "}
                    {nextUpcoming.reviewDate.toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                  </p>
                ) : (
                  <p className="text-xs text-gray-500 mt-1">Complete modules to start reviews.</p>
                )}
              </div>
            )}
          </div>

          {/* Study Now */}
          <button
            onClick={() =>
              currentStudy ? onViewChange(`${currentStudy.organId}-module-${currentStudy.moduleIndex}`) : null
            }
            disabled={!currentStudy}
            className="relative rounded-xl overflow-hidden shadow-lg hover:shadow-xl hover:scale-[1.02] transition-all duration-200 text-left disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
          >
            {/* Dark gradient background */}
            <div className="absolute inset-0 bg-gradient-to-br from-indigo-400 via-blue-500 to-violet-600" />
            {/* Radial glow */}
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_left,rgba(196,181,253,0.4),transparent_70%)]" />
            {/* Faded organ image */}
            {currentStudy?.customImage && (
              <img
                src={`/${currentStudy.customImage}`}
                alt=""
                aria-hidden="true"
                className="absolute bottom-0 right-0 h-24 w-auto object-contain opacity-20 pointer-events-none select-none"
              />
            )}

            <div className="relative z-10 p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <span className="text-xl">📖</span>
                  <h3 className="font-bold text-white text-base">Study Now</h3>
                </div>
                {currentStudy && (
                  <span className="text-[10px] font-bold bg-indigo-400/30 text-indigo-200 border border-indigo-400/40 px-2 py-0.5 rounded-full">
                    {currentStudy.completedCount}/{currentStudy.totalModules} done
                  </span>
                )}
              </div>

              {currentStudy ? (
                <>
                  <p className="text-xs text-indigo-200/70 mb-1">{currentStudy.organName}</p>
                  <p className="text-sm font-semibold text-white mb-3 leading-snug">{currentStudy.moduleName}</p>
                  {/* Progress bar */}
                  <div className="w-full bg-white/10 rounded-full h-1.5 mb-3">
                    <div
                      className="bg-indigo-300 h-1.5 rounded-full"
                      style={{ width: `${Math.round((currentStudy.completedCount / currentStudy.totalModules) * 100)}%` }}
                    />
                  </div>
                  <div className="inline-flex items-center gap-1.5 bg-indigo-400 hover:bg-indigo-300 text-indigo-900 font-bold text-xs px-3 py-1.5 rounded-lg shadow-md shadow-indigo-900/40 transition-colors">
                    Continue
                    <span className="text-sm">→</span>
                  </div>
                </>
              ) : (
                <p className="text-sm text-indigo-200">All modules completed! 🎉</p>
              )}
            </div>
          </button>

          {/* Daily Biology Fact */}
          <div className="relative rounded-xl overflow-hidden shadow-lg">
            <div className="absolute inset-0 bg-gradient-to-br from-cyan-400 via-teal-500 to-blue-600" />
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,rgba(103,232,249,0.4),transparent_70%)]" />
            <div className="absolute top-1 right-2 text-[64px] opacity-[0.10] pointer-events-none select-none leading-none">🔬</div>
            <div className="relative z-10 p-4">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-lg">🧬</span>
                <h3 className="font-bold text-white text-base">Daily Fact</h3>
                <span className="ml-auto text-[10px] font-bold bg-white/20 text-cyan-100 border border-white/25 px-2 py-0.5 rounded-full">
                  Biology
                </span>
              </div>
              <p className="text-sm font-semibold text-white/90 leading-relaxed">
                {getDailyFact()}
              </p>
            </div>
          </div>

          {/* Daily USMLE1 High-Yield Fact */}
          {(() => {
            const today = new Date();
            const dayOfYear = Math.floor(
              (today - new Date(today.getFullYear(), 0, 0)) / 1000 / 60 / 60 / 24
            );
            const dailyFact = usmle1Facts[dayOfYear % usmle1Facts.length];
            return (
              <div className="relative rounded-xl overflow-hidden shadow-lg">
                <div className="absolute inset-0 bg-gradient-to-br from-violet-500 via-purple-600 to-indigo-700" />
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(216,180,254,0.35),transparent_70%)]" />
                <div className="absolute top-0 right-2 text-[64px] opacity-[0.10] pointer-events-none select-none leading-none">⭐</div>
                <div className="relative z-10 p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className="text-lg">🏆</span>
                      <h3 className="font-bold text-white text-base">High Yield USMLE1</h3>
                    </div>
                    <span className="text-[10px] font-bold bg-white/20 text-purple-100 border border-white/25 px-2 py-0.5 rounded-full">
                      {dailyFact.category}
                    </span>
                  </div>
                  <p className="text-sm text-white/90 leading-relaxed">
                    {dailyFact.fact}
                  </p>
                </div>
              </div>
            );
          })()}
        </div>

        {/* Games Section + Awards Column */}
        <div className="flex flex-col gap-4">
          {/* Games Button */}
          <button
            onClick={() => onViewChange("games")}
            className="relative rounded-xl overflow-hidden shadow-lg hover:shadow-xl hover:scale-[1.02] transition-all duration-200 text-left"
          >
            {/* Dark gradient background */}
            <div className="absolute inset-0 bg-gradient-to-br from-emerald-700 via-green-800 to-teal-900" />

            {/* Subtle radial glow */}
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_left,rgba(74,222,128,0.25),transparent_70%)]" />

            {/* Gamer avatar — large, right-anchored, faded */}
            <img
              src="/avatar-gamer.png"
              alt=""
              aria-hidden="true"
              className="absolute bottom-0 right-0 h-28 w-auto object-contain opacity-30 pointer-events-none select-none"
            />

            {/* Content */}
            <div className="relative z-10 p-4">
              {/* Header row */}
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <span className="text-xl">🎮</span>
                  <h3 className="font-bold text-white text-base">Games</h3>
                </div>
                <span className="text-[10px] font-bold bg-green-400/30 text-green-200 border border-green-400/40 px-2 py-0.5 rounded-full">
                  6 games
                </span>
              </div>

              {/* Description */}
              <p className="text-xs text-green-100/80 mb-3">
                Test your clinical skills with interactive mini-games
              </p>

              {/* Game type icons */}
              <div className="flex items-center gap-2 mb-3">
                {["🔍","📋","🔤","🃏","🧪","🩺"].map((icon, i) => (
                  <span
                    key={i}
                    className="text-sm w-7 h-7 flex items-center justify-center bg-white/10 rounded-lg border border-white/15"
                  >
                    {icon}
                  </span>
                ))}
              </div>

              {/* Play now CTA */}
              <div className="inline-flex items-center gap-1.5 bg-green-400 hover:bg-green-300 text-green-900 font-bold text-xs px-3 py-1.5 rounded-lg shadow-md shadow-green-900/40 transition-colors">
                Play Now
                <span className="text-sm">→</span>
              </div>
            </div>
          </button>

          {/* Awards - One per organ */}
          <div className="bg-white rounded-xl p-3 shadow-sm border border-gray-200 flex flex-col h-64">
          <div className="flex items-center gap-2 mb-2">
            <Award className="w-4 h-4 text-amber-600" />
            <h3 className="font-semibold text-gray-800 text-xs">Awards</h3>
          </div>
          <div className="flex-1 overflow-y-auto space-y-1.5 pr-1 scrollbar-thin scrollbar-thumb-amber-300 scrollbar-track-gray-100">
            {userData.organProgress &&
            Object.keys(userData.organProgress).length > 0 ? (
              // Get organs that have completed modules
              Object.keys(userData.organProgress)
                .filter((organ) => {
                  const organProgress = userData.organProgress[organ];
                  return (
                    organProgress &&
                    organProgress.completed &&
                    organProgress.completed.length > 0
                  );
                })
                .map((organ) => {
                  // Get organ display name
                  const organNames = {
                    heart: "Heart",
                    brain: "Brain",
                    lungs: "Lungs",
                    colon: "Colon",
                    liver: "Liver",
                    kidney: "Kidneys",
                    womenreproductive: "Woman Reproductive",
                    skeleton: "Skeleton",
                    thyroid: "Thyroid Gland",
                    adrenalgland: "Adrenal Gland",
                  };

                  const organProgress = userData.organProgress[organ];

                  return (
                    <div
                      key={organ}
                      className="flex items-center gap-2 rounded-lg border border-amber-200 bg-gradient-to-r from-yellow-50 to-amber-50 p-1.5"
                    >
                      <div className="w-8 h-8 rounded overflow-hidden bg-white border border-amber-200 flex-shrink-0">
                        <img
                          src={`/organ-${organ}.png`}
                          alt={organNames[organ] || organ}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.target.style.display = "none";
                          }}
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-semibold text-gray-800 truncate">
                          {organNames[organ] || organ}
                        </div>
                        <div className="text-[10px] text-gray-600">
                          {organProgress.completed.length} module
                          {organProgress.completed.length !== 1 ? "s" : ""}
                        </div>
                      </div>
                    </div>
                  );
                })
            ) : (
              <div className="text-xs text-gray-600 text-center py-2">
                Complete modules to earn awards!
              </div>
            )}
          </div>
          </div>
        </div>

        {/* Streak Rank + Leaderboard column */}
        <div className="flex flex-col gap-4">

        {/* Streak Rank Card */}
        <div className="bg-gradient-to-br from-amber-50 to-yellow-100 rounded-xl p-4 shadow-sm border-2 border-amber-300">
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs font-bold text-amber-700 uppercase tracking-wide">Your Rank</span>
            <span className="text-xs text-amber-600 font-semibold">{streakRank.streak} day streak</span>
          </div>
          <div className="flex items-center gap-2 mb-3">
            <span className="text-2xl">{streakRank.currentRank.emoji}</span>
            <span className="text-sm font-bold text-gray-800">{streakRank.currentRank.title}</span>
          </div>
          {streakRank.nextRank ? (
            <>
              <div className="w-full bg-amber-200 rounded-full h-2 mb-2">
                <div
                  className="bg-amber-500 h-2 rounded-full transition-all duration-500"
                  style={{ width: `${streakRank.progress}%` }}
                />
              </div>
              <p className="text-xs text-gray-600">
                <span className="font-bold text-amber-700">{streakRank.daysToNext} day{streakRank.daysToNext !== 1 ? "s" : ""}</span>
                {" "}until{" "}
                <span className="font-semibold">{streakRank.nextRank.emoji} {streakRank.nextRank.title}</span>
              </p>
            </>
          ) : (
            <p className="text-xs font-bold text-amber-700 text-center">
              🏆 Highest rank achieved!
            </p>
          )}

          {/* Check-in Calendar */}
          <div className="mt-3 bg-white rounded-lg border border-amber-200">
            <div className="flex items-center justify-between px-3 py-2">
              <button
                onClick={() => setCalendarOffset(o => o - 1)}
                className="text-amber-500 hover:text-amber-700 transition-colors p-0.5"
                aria-label="Previous month"
              >
                <ChevronDown className="w-3 h-3 rotate-90" />
              </button>
              <button
                onClick={() => setIsCalendarOpen(!isCalendarOpen)}
                className="flex-1 text-xs font-semibold text-amber-700 hover:text-amber-900 transition-colors"
              >
                {calendar.monthName}
              </button>
              <button
                onClick={() => setCalendarOffset(o => Math.min(0, o + 1))}
                className="text-amber-500 hover:text-amber-700 transition-colors p-0.5 disabled:opacity-30"
                disabled={calendarOffset === 0}
                aria-label="Next month"
              >
                <ChevronDown className="w-3 h-3 -rotate-90" />
              </button>
            </div>
            {isCalendarOpen && (
              <div className="px-3 pb-3">
                <div className="grid grid-cols-7 gap-y-1">
                  {["M", "T", "W", "T", "F", "S", "S"].map((d, i) => (
                    <div key={i} className="text-center text-xs font-bold text-gray-400">
                      {d}
                    </div>
                  ))}
                  {calendar.cells.map((day, i) =>
                    day === null ? (
                      <div key={i} />
                    ) : (
                      <div
                        key={i}
                        className={`w-6 h-6 mx-auto rounded-full flex items-center justify-center text-xs font-semibold
                          ${day === calendar.todayDate ? "ring-2 ring-amber-400" : ""}
                          ${calendar.checkedIn.has(day)
                            ? "bg-amber-400 text-white"
                            : calendar.todayDate !== null && day > calendar.todayDate
                            ? "text-gray-300"
                            : "text-gray-400"
                          }`}
                      >
                        {day}
                      </div>
                    )
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Leaderboard with Top 3 Preview */}
        <div className="bg-gradient-to-br from-violet-50 to-purple-50 rounded-xl p-4 shadow-sm border-2 border-violet-200 flex flex-col h-full">
          <div className="flex items-center gap-2 mb-3">
            <Users className="w-5 h-5 text-violet-600" />
            <h3 className="font-semibold text-gray-800 text-sm">
              Top Performers
            </h3>
          </div>
          <div className="flex-1 overflow-y-auto space-y-2 mb-3">
            {topUsers.length === 0 ? (
              <p className="text-xs text-gray-400 text-center py-2">No data yet</p>
            ) : (
              topUsers.map((user, index) => (
                <div
                  key={user.id || user.name}
                  className={
                    "flex items-center gap-2 rounded-lg p-2 border " +
                    (user.isYou
                      ? "bg-violet-50 border-violet-300"
                      : "bg-white border-violet-100")
                  }
                >
                  <div
                    className={
                      "w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 " +
                      (index === 0
                        ? "bg-yellow-400 text-white"
                        : index === 1
                          ? "bg-gray-300 text-white"
                          : "bg-orange-400 text-white")
                    }
                  >
                    {index + 1}
                  </div>
                  <AvatarPreview avatar={user.avatar} size="sm" />
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-semibold text-gray-800 truncate">
                      {user.name}{user.isYou && <span className="ml-1 text-violet-500">you</span>}
                    </div>
                    <div className="text-xs text-gray-500">
                      {calcMomentum(user.weeklyXP, user.streak)} pts this week
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
          <button
            onClick={() => onViewChange("leaderboard")}
            className="w-full bg-gradient-to-br from-violet-400 to-purple-800 text-white rounded-lg py-2 text-sm font-semibold hover:shadow-md transition-shadow"
          >
            View Full Leaderboard
          </button>
        </div>

        </div>{/* end streak rank + leaderboard column */}
      </div>

      <h2 className="text-2xl font-bold text-white mb-6">Organ Modules</h2>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {organs.map((organ) => {
          const OrganIcon = organ.icon;
          return (
            <button
              key={organ.id}
              onClick={() => organ.comingSoon ? undefined : onViewChange(organ.id + "-overview")}
              disabled={organ.comingSoon}
              className={
                "rounded-xl p-6 shadow-sm border-2 text-left bg-gradient-to-br relative overflow-hidden " +
                organ.gradient + " " + organ.borderColor +
                (organ.comingSoon
                  ? " opacity-60 cursor-not-allowed grayscale-[30%]"
                  : " transition-all transform hover:shadow-xl hover:scale-105 hover:-translate-y-1 " + organ.hoverGradient)
              }
            >
              {/* Background image - zoomed and shadowed */}
              {organ.customImage && (
                <div className="absolute inset-0 opacity-20 overflow-hidden flex items-center justify-center">
                  <img
                    src={`/${organ.customImage}`}
                    alt=""
                    className="blur-sm"
                    style={{
                      filter: "brightness(0.7)",
                      width: "300%",
                      height: "300%",
                      objectFit: "cover"
                    }}
                  />
                </div>
              )}

              {/* Coming Soon badge */}
              {organ.comingSoon && (
                <div className="absolute top-3 right-3 z-20 bg-gray-700/80 text-white text-[10px] font-bold px-2 py-0.5 rounded-full tracking-wide">
                  Coming Soon
                </div>
              )}

              {/* Foreground image - clear icon */}
              <div className="relative z-10 mb-4 flex justify-center">
                {organ.customImage ? (
                  <div className="bg-white bg-opacity-80 rounded-lg p-2 shadow-md border-2 border-white/50">
                    <img
                      src={`/${organ.customImage}`}
                      alt={organ.name}
                      className="w-24 h-24 object-contain"
                    />
                  </div>
                ) : (
                  <div className={"inline-block p-3 rounded-full " + organ.iconColor}>
                    <OrganIcon className="w-8 h-8" />
                  </div>
                )}
              </div>

              {/* Text content */}
              <h3 className="text-lg font-bold text-gray-800 mb-2 relative z-10">
                {organ.name}
              </h3>
              <div className="text-sm text-gray-600">
                {organ.comingSoon ? (
                  <span className="italic">Content coming soon</span>
                ) : (
                  <>
                    {userData.organProgress[organ.id] &&
                    userData.organProgress[organ.id].completed
                      ? userData.organProgress[organ.id].completed.length
                      : 0}
                    /{organ.totalModules} modules
                  </>
                )}
              </div>
            </button>
          );
        })}
      </div>

      <h2 className="text-2xl font-bold text-white mb-6 mt-10">
        Paraclinical Modules
      </h2>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {paraclinicalModules.map((module) => {
          const ModuleIcon = module.icon;
          return (
            <button
              key={module.id}
              onClick={() => module.comingSoon ? undefined : onViewChange(module.id + "-overview")}
              disabled={module.comingSoon}
              className={
                "rounded-xl p-6 shadow-sm border-2 text-left bg-gradient-to-br relative overflow-hidden " +
                module.gradient + " " + module.borderColor +
                (module.comingSoon
                  ? " opacity-60 cursor-not-allowed grayscale-[30%]"
                  : " transition-all transform hover:shadow-xl hover:scale-105 hover:-translate-y-1 " + module.hoverGradient)
              }
            >
              {module.customImage && (
                <div className="absolute inset-0 opacity-20 overflow-hidden flex items-center justify-center">
                  <img
                    src={`/${module.customImage}`}
                    alt=""
                    className="blur-sm"
                    style={{ filter: "brightness(0.7)", width: "300%", height: "300%", objectFit: "cover" }}
                  />
                </div>
              )}
              {module.comingSoon && (
                <div className="absolute top-3 right-3 z-20 bg-gray-700/80 text-white text-[10px] font-bold px-2 py-0.5 rounded-full tracking-wide">
                  Coming Soon
                </div>
              )}
              <div className="relative z-10 mb-4 flex justify-center">
                {module.customImage ? (
                  <div className="bg-white bg-opacity-80 rounded-lg p-2 shadow-md border-2 border-white/50">
                    <img
                      src={`/${module.customImage}`}
                      alt={module.name}
                      className="w-24 h-24 object-contain"
                    />
                  </div>
                ) : (
                  <div className={"inline-block p-3 rounded-full " + module.iconColor}>
                    <ModuleIcon className="w-8 h-8" />
                  </div>
                )}
              </div>
              <h3 className="text-lg font-bold text-gray-800 mb-2 relative z-10">
                {module.name}
              </h3>
              <div className="text-sm text-gray-600">
                {module.comingSoon ? (
                  <span className="italic">Content coming soon</span>
                ) : (
                  <>
                    {userData.organProgress[module.id]?.completed?.length || 0}
                    /{module.totalModules} modules
                  </>
                )}
              </div>
            </button>
          );
        })}
      </div>

      <h2 className="text-2xl font-bold text-white mb-6 mt-10">
        High Yield Modules
      </h2>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {highYieldModules.map((module) => {
          const ModuleIcon = module.icon;
          return (
            <button
              key={module.id}
              onClick={() => module.comingSoon ? undefined : onViewChange(module.id + "-overview")}
              disabled={module.comingSoon}
              className={
                "rounded-xl p-6 shadow-sm border-2 text-left bg-gradient-to-br relative overflow-hidden " +
                module.gradient + " " + module.borderColor +
                (module.comingSoon
                  ? " opacity-60 cursor-not-allowed grayscale-[30%]"
                  : " transition-all transform hover:shadow-xl hover:scale-105 hover:-translate-y-1 " + module.hoverGradient)
              }
            >
              {module.comingSoon && (
                <div className="absolute top-3 right-3 z-20 bg-gray-700/80 text-white text-[10px] font-bold px-2 py-0.5 rounded-full tracking-wide">
                  Coming Soon
                </div>
              )}
              <div className="relative z-10 mb-4 flex justify-center">
                <div className={"inline-block p-3 rounded-full " + module.iconColor}>
                  <ModuleIcon className="w-8 h-8" />
                </div>
              </div>

              <h3 className="text-lg font-bold text-gray-800 mb-2 relative z-10">
                {module.name}
              </h3>
              <div className="text-sm text-gray-600">
                {module.comingSoon ? (
                  <span className="italic">Content coming soon</span>
                ) : (
                  <>
                    {userData.organProgress[module.id]?.completed?.length || 0}
                    /{module.totalModules} modules
                  </>
                )}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default Dashboard;
